import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { ParticipantStep } from "../../shared/types.ts";

// ShareVoice's FormExecutor/autoAdvance.ts: instant choices acknowledge the tap
// for 400ms. Numeric ratings in this catalog also represent sliders, so only
// single-value option fields qualify; grouped forms and capture stay explicit.
const ACKNOWLEDGEMENT_MS = 400;
export const choiceAutoAdvances = (step: ParticipantStep): boolean =>
  step.fields.length === 1 &&
  step.fields[0].kind === "select" &&
  !step.fields[0].inline &&
  !step.elaborationPrompt;

/** Only a locally accepted choice can schedule an advance, never a mirrored answer. */
export function useChoiceAdvance({
  disabled,
  controlRevision,
  advance,
}: {
  disabled: boolean;
  controlRevision: number;
  advance: () => Promise<boolean>;
}) {
  const [status, setStatus] = useState<"idle" | "pending" | "advancing">(
    "idle",
  );
  const generation = useRef(0),
    timer = useRef<number | undefined>(undefined),
    latest = useRef({ disabled, advance });
  useLayoutEffect(() => {
    latest.current = { disabled, advance };
  });
  const invalidate = useCallback(() => {
    generation.current++;
    window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);
  // A takeover, connection pause, hidden preview, or unmount invalidates both
  // the timer and any answer request that has not yet returned.
  useLayoutEffect(() => {
    invalidate();
    setStatus("idle");
    return invalidate;
  }, [disabled, controlRevision, invalidate]);

  const select = (accepted: Promise<boolean>) => {
    invalidate();
    if (latest.current.disabled) return;
    const ticket = generation.current,
      selectedAt = performance.now();
    setStatus("pending");
    const release = () => {
      if (ticket === generation.current) setStatus("idle");
    };
    void accepted.then((ok) => {
      if (ticket !== generation.current) return;
      if (!ok || latest.current.disabled) {
        release();
        return;
      }
      timer.current = window.setTimeout(
        () => {
          timer.current = undefined;
          if (ticket !== generation.current || latest.current.disabled) return;
          setStatus("advancing");
          void latest.current.advance().then((moved) => {
            // Keep Continue hidden until the new projection arrives on success.
            // A rejected advance exposes the ordinary explicit retry affordance.
            if (!moved) release();
          }, release);
        },
        Math.max(0, ACKNOWLEDGEMENT_MS - (performance.now() - selectedAt)),
      );
    }, release);
  };
  return {
    select,
    pending: status !== "idle",
    advancing: status === "advancing",
  };
}
